/**
 * Platform detection utility
 * Used to determine the current runtime environment
 */
import logger from './logger';

// Check if we're in a Render.com deployment or other web-only environment
const isRenderDeployment = (): boolean => {
  return process.env.REACT_APP_DEPLOYMENT === 'render' || 
         process.env.REACT_APP_DISABLE_CAPACITOR === 'true';
};

/**
 * Check if the app is running in a Capacitor environment (iOS/Android)
 */
export const isCapacitorPlatform = (): boolean => {
  // CRITICAL: Check for Capacitor FIRST before checking build-time env vars
  // Runtime detection takes priority over build configuration
  if (typeof window !== 'undefined' && (window as any).Capacitor !== undefined) {
    logger.info('*** Platform detection: Capacitor detected (runtime) ***');
    return true;
  }

  // Only check deployment env vars if Capacitor is not present
  if (isRenderDeployment()) {
    logger.info('*** Platform detection: Render.com deployment detected, no Capacitor found ***');
    return false;
  }
  
  // Fallback check for mobile platforms using user agent
  if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
    const userAgent = navigator.userAgent || '';
    logger.info('*** Platform detection: User agent - ' + userAgent + ' ***');
    
    // Check for mobile device based on user agent
    const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);
    if (isMobile) {
      logger.info('*** Platform detection: Mobile device detected via user agent ***');
      return true;
    }
    
    // Check for Ionic webview
    if (userAgent.indexOf('ionic') !== -1 || userAgent.indexOf('Capacitor') !== -1) {
      logger.info('*** Platform detection: Ionic/Capacitor webview detected ***');
      return true;
    }
  }
  
  // Force mobile platform detection in Capacitor builds
  if (typeof window !== 'undefined' && window.location.href.indexOf('capacitor://') !== -1) {
    logger.info('*** Platform detection: Capacitor URL scheme detected ***');
    return true;
  }
  
  // Explicitly enable detection for iOS simulator
  if (typeof navigator !== 'undefined' && 
     (navigator.platform === 'iPhone' || 
      navigator.platform === 'iPad' || 
      navigator.platform === 'iPod' ||
      (navigator.platform.indexOf('Mac') !== -1 && typeof window !== 'undefined' && 'ontouchend' in document))) {
    logger.info('*** Platform detection: iOS device/simulator detected ***');
    return true;
  }
  
  logger.info('*** Platform detection: Not a mobile platform ***');
  return false;
};

/**
 * Check if the app is running on iOS platform
 * Enhanced to detect iOS even when Capacitor platform detection might fail
 */
export const isIOSPlatform = (): boolean => {
  // CRITICAL: Check for Capacitor iOS FIRST before checking build-time env vars
  // Runtime detection takes priority over build configuration
  if (typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform) {
    const platform = (window as any).Capacitor.getPlatform();
    if (platform === 'ios') {
      logger.info('*** iOS detection: Detected via Capacitor getPlatform() (runtime) ***');
      return true;
    }
  }

  // Only check deployment env vars if Capacitor iOS is not detected
  if (isRenderDeployment()) {
    return false;
  }

  // User agent detection for iOS
  if (typeof navigator !== 'undefined') {
    const userAgent = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/i.test(userAgent) && !(window as any).MSStream) {
      logger.info('*** iOS detection: Detected via iOS user agent ***');
      return true;
    }
  }

  // Platform detection for iOS devices/simulator
  if (typeof navigator !== 'undefined' &&
      (navigator.platform === 'iPhone' ||
       navigator.platform === 'iPad' ||
       navigator.platform === 'iPod' ||
       (navigator.platform.indexOf('Mac') !== -1 && typeof window !== 'undefined' && 'ontouchend' in document))) {
    logger.info('*** iOS detection: Detected via platform properties ***');
    return true;
  }

  return false;
};

/**
 * Check if the app is running on Android platform
 */
export const isAndroidPlatform = (): boolean => {
  // CRITICAL: Check for Capacitor Android FIRST before checking build-time env vars
  if (typeof window !== 'undefined' && (window as any).Capacitor && (window as any).Capacitor.getPlatform) {
    try {
      const platform = (window as any).Capacitor.getPlatform();
      if (platform === 'android') {
        logger.info('*** Android detection: Detected via Capacitor getPlatform() (runtime) ***');
        return true;
      }
    } catch (error) {
      logger.error('Error getting platform:', error);
    }
  }

  // Only check deployment env vars if Capacitor Android is not detected
  if (isRenderDeployment()) {
    return false;
  }

  return false;
};

/**
 * Check if app is running in a browser (web) environment
 */
export const isWebPlatform = (): boolean => {
  // Always return true for Render.com deployment
  if (isRenderDeployment()) {
    return true;
  }
  
  return !isCapacitorPlatform();
};

/**
 * Get the current platform as a string
 */
export const getCurrentPlatform = (): 'ios' | 'android' | 'web' => {
  // CRITICAL: Check runtime platform FIRST before checking build-time env vars
  if (isIOSPlatform()) return 'ios';
  if (isAndroidPlatform()) return 'android';

  // Only return 'web' after checking for native platforms
  return 'web';
};

const exportedObject = {
  isCapacitorPlatform,
  isIOSPlatform,
  isAndroidPlatform,
  isWebPlatform,
  getCurrentPlatform
};

export default exportedObject;